import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { trackEvent, ANALYTICS_EVENTS } from "@/lib/analytics";

const ROLES = [
  "Digital product creator",
  "E-commerce seller",
  "Content creator",
  "Marketer / agency",
];

const NICHES = [
  "Health & wellness",
  "Finance & investing",
  "AI & tech",
  "Beauty & fashion",
  "Home & lifestyle",
  "Education & coaching",
  "Gaming",
  "Pets",
  "Sustainability",
];

const GOALS = [
  "Launch a new product",
  "Scale an existing offer",
  "Grow content & audience",
  "Find winning ad angles",
];

const EXPERIENCE = ["Just starting", "1–2 launches", "Seasoned operator"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [role, setRole] = useState<string>("");
  const [niches, setNiches] = useState<string[]>([]);
  const [goal, setGoal] = useState<string>("");
  const [experience, setExperience] = useState<string>("");

  if (!authLoading && !user) {
    navigate("/auth", { replace: true });
    return null;
  }

  const toggleNiche = (n: string) => {
    setNiches((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n].slice(0, 5)
    );
  };

  const finish = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("user_preferences").upsert(
        {
          user_id: user.id,
          role: role || null,
          niches,
          primary_goal: goal || null,
          experience_level: experience || null,
          onboarded_at: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      );
      if (error) throw error;
      trackEvent(ANALYTICS_EVENTS.ONBOARDING_COMPLETED, {
        role: role || "unspecified",
        goal: goal || "unspecified",
        experience: experience || "unspecified",
        niche_count: niches.length,
      });
      toast.success("Tailoring your trend feed");
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toast.error(err.message || "Could not save preferences");
    } finally {
      setSaving(false);
    }
  };

  // ── Step content ──────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <Intro
            tag="// STEP 01"
            title="Scan 35+ secret sources"
            body="Trends Discovery quietly monitors marketplaces, forums, ad libraries, and creator platforms in real-time — surfacing demand before it goes mainstream."
          />
        );
      case 1:
        return (
          <div>
            <SectionHeader tag="// QUESTION 01" title="What best describes you?" />
            <ChoiceGrid
              options={ROLES}
              selected={role ? [role] : []}
              onSelect={(v) => setRole((prev) => (prev === v ? "" : v))}
            />
          </div>
        );
      case 2:
        return (
          <div>
            <SectionHeader
              tag="// QUESTION 02"
              title="Which niches do you want trends from?"
              hint="Pick up to 5"
            />
            <ChoiceGrid options={NICHES} selected={niches} onSelect={toggleNiche} multi />
          </div>
        );
      case 3:
        return (
          <div>
            <SectionHeader tag="// QUESTION 03" title="What's your primary goal?" />
            <ChoiceGrid
              options={GOALS}
              selected={goal ? [goal] : []}
              onSelect={(v) => setGoal((prev) => (prev === v ? "" : v))}
            />
            <div className="mt-6">
              <SectionHeader tag="// QUESTION 04" title="Where are you today?" />
              <ChoiceGrid
                options={EXPERIENCE}
                selected={experience ? [experience] : []}
                onSelect={(v) => setExperience((prev) => (prev === v ? "" : v))}
              />
            </div>
          </div>
        );
      case 4:
        return (
          <Intro
            tag="// STEP 02"
            title="Tomorrow's best-sellers, ranked for you"
            body="Our AI scores every emerging niche by velocity, competition, and monetization potential — filtered to your interests so you skip the guesswork."
          />
        );
      case 5:
        return (
          <Intro
            tag="// STEP 03"
            title="Launch with a complete blueprint"
            body="Every trend ships with an actionable product blueprint: angles, audiences, positioning, and offer structure. Less research, more launching."
          />
        );
      default:
        return null;
    }
  };

  const totalSteps = 6;
  const isLast = step === totalSteps - 1;
  const canAdvance = (() => {
    if (step === 1) return !!role;
    if (step === 2) return niches.length > 0;
    if (step === 3) return !!goal && !!experience;
    return true;
  })();

  const ctaLabel = (() => {
    if (isLast) return saving ? "Opening…" : "Open my dashboard";
    if (step === 0) return "Tailor my feed";
    if (step === 3) return "See what's next";
    return "Continue";
  })();

  const next = () => {
    if (isLast) return finish();
    setStep(step + 1);
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10 bg-hero-radial">
      <div className="w-full max-w-xl rounded-2xl border border-border bg-card p-8 md:p-10 shadow-elevated">
        <div className="flex items-center justify-between mb-8">
          <div className="font-mono text-xs text-primary">
            {step + 1} / {totalSteps}
          </div>
          <div className="flex gap-1.5">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 w-6 rounded-full transition-colors ${
                  i <= step ? "bg-primary" : "bg-border"
                }`}
              />
            ))}
          </div>
        </div>

        {renderStep()}

        {(() => {
          const hint = !canAdvance
            ? step === 1
              ? "Pick the option that best describes you to continue."
              : step === 2
              ? "Pick at least one niche to continue."
              : step === 3
              ? !goal && !experience
                ? "Pick a primary goal and where you are today to continue."
                : !goal
                ? "Pick a primary goal to continue."
                : "Pick where you are today to continue."
              : null
            : null;
          return hint ? (
            <p
              role="status"
              data-testid="onboarding-hint"
              className="mt-6 text-sm text-muted-foreground"
            >
              {hint}
            </p>
          ) : null;
        })()}

        <div className="mt-10 flex items-center justify-between">
          {step > 0 ? (
            <button
              onClick={() => setStep(step - 1)}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              ← Back
            </button>
          ) : (
            <button
              onClick={() => navigate("/dashboard", { replace: true })}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Skip
            </button>
          )}
          <Button onClick={next} variant="hero" size="lg" disabled={!canAdvance || saving}>
            {ctaLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Intro({ tag, title, body }: { tag: string; title: string; body: string }) {
  return (
    <div>
      <div className="font-mono text-xs text-primary">{tag}</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold mt-4">{title}</h1>
      <p className="mt-4 text-muted-foreground leading-relaxed">{body}</p>
    </div>
  );
}

function SectionHeader({
  tag,
  title,
  hint,
}: {
  tag: string;
  title: string;
  hint?: string;
}) {
  return (
    <div className="mb-4">
      <div className="font-mono text-xs text-primary">{tag}</div>
      <h2 className="font-display text-2xl font-bold mt-2">{title}</h2>
      {hint && <p className="text-xs text-muted-foreground mt-1 font-mono">{hint}</p>}
    </div>
  );
}

function ChoiceGrid({
  options,
  selected,
  onSelect,
  multi = false,
}: {
  options: string[];
  selected: string[];
  onSelect: (value: string) => void;
  multi?: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map((opt) => {
        const active = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onSelect(opt)}
            className={`text-left text-sm rounded-lg border px-3 py-2.5 transition-colors ${
              active
                ? "border-primary bg-primary/10 text-foreground"
                : "border-border bg-background hover:border-primary/50"
            }`}
            aria-pressed={active}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}
